import {
  Box,
  Heading,
  Text,
  Flex,
  useToast,
  useDisclosure,
  Select,
  Button,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { finleyAssert, path as commonPath } from '@finley-technologies/common';
import { FinleyAi, typeguards } from '@finley-technologies/finley-types';

import { SchemaEditor } from '../../../common-frontend/components/SchemaEditor';
import { useFinleyAiVirtualSheetDirectUpload } from '../../hooks/virtualSheetDirectUpload';
import { FinleyAiViewContext, VirtualSheetViewContext } from '../types';
import { PRE_CONFIGURED_TEMPLATES } from './Templates';
import { UploadIconButton } from 'packages/common-frontend/components/IconButton';
import { UploadModal } from 'packages/common-frontend/components/ReactFlow/UploadModal';
import { z } from 'zod/v4';

const QuestionConfig = ({
  questionConfig,
  setViewContext,
  isLocked: _unused, // @makarand-virtualSheet: disable editing when locked
}: {
  questionConfig: FinleyAi.FinleyAiVirtualSheetFormulaQuestions;
  setViewContext: (value: React.SetStateAction<FinleyAiViewContext>) => void;
  isLocked: boolean;
}) => {
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');

  const jsonSchema = useMemo(() => {
    return z.toJSONSchema(FinleyAi.finleyAiVirtualSheetFormulaQuestionsSchema, {
      target: 'draft-7',
    });
  }, []);

  const loadTemplate = useCallback(() => {
    const template = PRE_CONFIGURED_TEMPLATES.find(
      (t) => t.name === selectedTemplate,
    );
    if (template) {
      setViewContext((viewContext) => {
        finleyAssert(
          viewContext.type === 'virtualSheetConfig',
          'View Context must be virtual sheet config.',
        );
        return {
          ...viewContext,
          questionConfigs: template.config,
        };
      });
      setSelectedTemplate('');
    }
  }, [selectedTemplate, setViewContext]);

  return (
    <Box p={4} gap={4}>
      <VStack spacing={4} align="stretch">
        {/* Template Selection */}
        <Box>
          <Text mb={2} fontWeight="medium">
            Load Pre-configured Template:
          </Text>
          <Flex gap={2}>
            <Select
              placeholder="Select a template..."
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              flex={1}
            >
              {PRE_CONFIGURED_TEMPLATES.map((template) => (
                <option key={template.name} value={template.name}>
                  {template.name}
                </option>
              ))}
            </Select>
            <Button
              onClick={loadTemplate}
              isDisabled={!selectedTemplate}
              colorScheme="blue"
              size="md"
            >
              Load Template
            </Button>
          </Flex>
        </Box>

        {/* Schema Editor */}
        <Box>
          <SchemaEditor
            direction="vertical"
            contents={questionConfig}
            nodeId="temp"
            newConfig={false}
            schema={jsonSchema}
            onSave={async (_id: string, newValue: object) => {
              const parsed = z.parse(
                FinleyAi.finleyAiVirtualSheetFormulaQuestionsSchema,
                newValue,
              );
              setViewContext((viewContext) => {
                finleyAssert(
                  viewContext.type === 'virtualSheetConfig',
                  'View Context must be virtual sheet config.',
                );
                return {
                  ...viewContext,
                  questionConfigs: parsed,
                };
              });
            }}
          />
        </Box>
      </VStack>
    </Box>
  );
};

export const FinleyVirtualSheetInputs = ({
  targetOrganizationId,
  isLocked,
  viewContext,
  setViewContext,
}: {
  targetOrganizationId: string;
  isLocked: boolean;
  viewContext: VirtualSheetViewContext;
  setViewContext: (value: React.SetStateAction<FinleyAiViewContext>) => void;
}) => {
  const toast = useToast();

  const { isOpen, onClose, onOpen } = useDisclosure();

  const { directUpload } = useFinleyAiVirtualSheetDirectUpload({
    targetOrganizationId,
  });

  const upload = useCallback(
    async (file: File) => {
      try {
        const fileExtension = commonPath.getFileExtension(file.name);
        const documentUploaded = commonPath.basename(file.name);

        finleyAssert(
          typeguards.finleyAi.isFinleyAiVirtualSheetSupportedFileExtension(
            fileExtension,
          ),
          `[fileExtension:${fileExtension}] is not a supported file extension.`,
        );
        const { s3Bucket, s3Path } = await directUpload({
          file,
          documentName: documentUploaded,
          fileExtension,
        });
        setViewContext((vc) => ({
          ...vc,
          s3Bucket,
          s3Path,
          extractionEngine: 'default', // set engine to default on new upload, in event file ext changes
        }));
      } catch (e) {
        toast({
          title: 'Document upload failed. Please see technical assistance.',
          status: 'error',
          position: 'top',
          isClosable: true,
        });
      }
    },
    [directUpload, toast, setViewContext],
  );

  return (
    <Box mr="20px">
      <Heading as="h4" size="md" mb="3">
        VirtualSheet Configuration
      </Heading>
      <Flex mb="3" alignItems="center">
        <Text>
          Loaded Document:{' '}
          {viewContext.s3Path.split('/').at(-1) ||
            'Load document prior to continuing.'}
        </Text>
        <UploadIconButton
          ariaLabel="upload"
          label="upload"
          onClick={onOpen}
          isDisabled={isLocked}
        />
        <UploadModal
          modalHeaderText="Upload Document"
          onClose={onClose}
          isOpen={isOpen}
          handleFile={upload}
          allowedExtensions={['xlsx', 'xlsb']}
        />
      </Flex>
      <Flex alignItems="center" mb="3">
        <Heading as="h5" size="md">
          Question Configurations
        </Heading>
      </Flex>
      <QuestionConfig
        questionConfig={viewContext.questionConfigs}
        setViewContext={setViewContext}
        isLocked={isLocked}
      />
    </Box>
  );
};
